import React, { useState, useEffect, useRef } from 'react';
import { MemoryRouter } from 'react-router-dom';
import { getMemberSession, createMember, memberLogin, memberLogout } from '../memberStore';
import { getAllDevices } from '../traccar';
import { requestShare } from '../carCode';
import { memberLinkStatus } from '../memberLink';
import CarPage from './CarPage';
import AlertWatcher from '../AlertWatcher';
import { TagAlongWordmark } from '../TagAlongLogo';

const card = { background: 'white', borderRadius: '14px', padding: '18px', boxShadow: '0 1px 3px rgba(15,23,42,0.08)' };
const inp = { padding: '11px 13px', border: '1px solid #d1d5db', borderRadius: '9px', fontSize: '15px', width: '100%', boxSizing: 'border-box', background: '#fff' };
const lbl = { fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '4px' };
const btn = { background: 'linear-gradient(135deg,#0a84ff,#0060df)', color: 'white', fontWeight: 700, fontSize: '14px', border: 'none', borderRadius: '9px', padding: '11px 18px', cursor: 'pointer' };

export default function FamilyPage() {
  const [member, setMember] = useState(getMemberSession());
  const [mode, setMode] = useState('login'); // 'login' | 'signup'
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [authErr, setAuthErr] = useState('');
  const [devices, setDevices] = useState([]);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const openRef = useRef(false);

  // pre-fill the code from a scanned QR / shared link (?add=TA-XXXX)
  useEffect(() => {
    try { const m = (window.location.search || '').match(/[?&]add=([^&]+)/); if (m) setCode(decodeURIComponent(m[1])); } catch { /* ignore */ }
  }, []);

  // keep the device list fresh so approvals show up (paused while adding a car)
  useEffect(() => {
    if (!member) return undefined;
    let live = true;
    const load = () => { if (!openRef.current) getAllDevices().then((d) => { if (live) setDevices(d || []); }).catch(() => {}); };
    load();
    const t = setInterval(load, 12000);
    return () => { live = false; clearInterval(t); };
  }, [member]);

  const doAuth = async (e) => {
    e.preventDefault();
    setAuthErr('');
    try {
      const m = mode === 'signup' ? await createMember(form) : await memberLogin(form.email, form.password);
      setMember(m);
    } catch (err) { setAuthErr(err.message); }
  };
  const signOut = () => { memberLogout(); setMember(null); setDevices([]); };

  const addCar = async () => {
    const c = code.trim();
    if (!c || busy) return;
    setBusy(true); setMsg(''); openRef.current = true;
    try {
      const r = await requestShare(c);
      if (r.status === 'approved') { setMsg('✓ You already have access to that car.'); }
      else if (r.status === 'pending') { setMsg('⏳ You already requested that car — waiting on the owner to approve.'); }
      else { setMsg('✅ Request sent! The owner will get a notification to approve you. Access appears here once they do.'); }
      setCode('');
      const d = await getAllDevices(); setDevices(d || []);
    } catch (e) { setMsg(e && e.notFound ? '❌ No car found with that code. Double-check it with the owner.' : '❌ Couldn’t send the request. Try again in a moment.'); }
    setBusy(false); openRef.current = false;
  };

  // cars the owner has approved for this member, and ones still pending
  const approved = member ? devices.filter((d) => memberLinkStatus(d, member.id) === 'approved') : [];
  const pending = member ? devices.filter((d) => memberLinkStatus(d, member.id) === 'pending') : [];
  const approvedIds = approved.map((d) => String(d.id));

  // ---------- not signed in ----------
  if (!member) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg,#eef2ff,#f8fafc)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ ...card, width: 'min(420px, 96vw)', padding: '26px' }}>
          <div style={{ textAlign: 'center', marginBottom: '6px' }}><TagAlongWordmark /></div>
          <h2 style={{ fontFamily: 'Poppins, sans-serif', fontSize: '20px', margin: '10px 0 2px', textAlign: 'center' }}>Family & shared access</h2>
          <p style={{ fontSize: '13px', color: '#64748b', textAlign: 'center', margin: '0 0 18px' }}>
            Sign in to see cars a family member has shared with you — same live tracking, alerts, and chat as they see.
          </p>
          <form onSubmit={doAuth}>
            {mode === 'signup' && (
              <div style={{ marginBottom: '12px' }}><span style={lbl}>Your name</span><input style={inp} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Jane Doe" /></div>
            )}
            <div style={{ marginBottom: '12px' }}><span style={lbl}>Email</span><input style={inp} type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="you@email.com" /></div>
            <div style={{ marginBottom: '14px' }}><span style={lbl}>Password</span><input style={inp} type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="••••••••" /></div>
            {authErr && <div style={{ fontSize: '12.5px', color: '#b91c1c', marginBottom: '10px' }}>{authErr}</div>}
            <button type="submit" style={{ ...btn, width: '100%' }}>{mode === 'signup' ? 'Create account' : 'Sign in'}</button>
          </form>
          <div style={{ textAlign: 'center', marginTop: '14px', fontSize: '13px', color: '#64748b' }}>
            {mode === 'signup' ? 'Already have an account?' : 'New here?'}{' '}
            <button onClick={() => { setMode(mode === 'signup' ? 'login' : 'signup'); setAuthErr(''); }} style={{ background: 'transparent', border: 'none', color: '#0a84ff', fontWeight: 700, cursor: 'pointer' }}>
              {mode === 'signup' ? 'Sign in' : 'Create one'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- signed in ----------
  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>
      {/* scoped alerts for approved cars only */}
      {approvedIds.length > 0 && (
        <MemoryRouter><AlertWatcher deviceIds={approvedIds} /></MemoryRouter>
      )}
      <div style={{ background: 'white', borderBottom: '1px solid #e2e8f0', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '12px', position: 'sticky', top: 0, zIndex: 40 }}>
        <TagAlongWordmark />
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: '13px', color: '#334155' }}>Signed in as <b>{member.name || member.email}</b></div>
        <button onClick={signOut} style={{ background: '#e2e8f0', color: '#334155', border: 'none', borderRadius: '8px', padding: '7px 12px', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}>Sign out</button>
      </div>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '18px' }}>
        {/* add a car by code */}
        <div style={{ ...card, marginBottom: '16px', background: 'linear-gradient(160deg,#eff6ff,#ffffff)', border: '1px solid #dbeafe' }}>
          <div style={{ fontWeight: 800, fontFamily: 'Poppins, sans-serif', fontSize: '15px', color: '#0f172a', marginBottom: '2px' }}>➕ Add a car</div>
          <div style={{ fontSize: '12.5px', color: '#475569', marginBottom: '10px' }}>Enter the share code the car’s owner gave you. They’ll approve your request, then the car appears below with full access.</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <input style={{ ...inp, flex: 1, minWidth: '180px', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '1px' }} placeholder="TA-XXXXXXX" value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addCar(); }} />
            <button onClick={addCar} disabled={busy || !code.trim()} style={{ ...btn, opacity: busy || !code.trim() ? 0.6 : 1 }}>{busy ? 'Sending…' : 'Request access'}</button>
          </div>
          {msg && <div style={{ fontSize: '12.5px', fontWeight: 600, color: msg.startsWith('✅') || msg.startsWith('✓') ? '#15803d' : msg.startsWith('⏳') ? '#b45309' : '#b91c1c', marginTop: '10px' }}>{msg}</div>}
          {pending.length > 0 && (
            <div style={{ marginTop: '12px', fontSize: '12.5px', color: '#b45309' }}>
              ⏳ Waiting on approval: {pending.map((d) => (d.attributes || {}).displayName || d.name).join(', ')}
            </div>
          )}
        </div>

        {/* the shared cars — full owner-style view */}
        {approvedIds.length === 0 ? (
          <div style={{ ...card, textAlign: 'center', color: '#64748b' }}>
            <div style={{ fontSize: '32px', marginBottom: '6px' }}>🚗</div>
            No shared cars yet. Add one with a code above — once the owner approves, you’ll see it here with the same live tracking, alerts and chat they have.
          </div>
        ) : (
          <CarPage restrictIds={approvedIds} />
        )}
      </div>
    </div>
  );
}
